"use client";
import { useEffect, useRef, useState, useTransition } from "react";
import type { CardRecord } from "@/lib/posts-helpers";
import { StickerCard } from "@/components/stickers/StickerCard";
import {
  inGridAdNode,
  inGridAdsForRecordCount,
  shouldInsertInGridAdAfter,
} from "@/components/ads/inGridAdCadence";
import { cn } from "@/lib/utils";

export interface LoadMoreFetcher {
  (page: number): Promise<{ records: CardRecord[]; hasNext: boolean }>;
}

interface LoadMoreClientProps {
  initialPage: number;
  initialHasNext: boolean;
  fetcher: LoadMoreFetcher;
  // When true, inject the same in-grid ad cadence StickerGrid uses on
  // page 1 into appended pages, with the global cap honoured across pages.
  withInGridAds?: boolean;
  // Number of records the SSR'd grid (StickerGrid) already rendered. Used
  // to keep ad cadence contiguous across the page boundary and to seed the
  // running ad count for the cap check.
  initialRecordCount?: number;
  // Records from page 1 that the server chose not to SSR (above-the-fold
  // budget). Rendered on the first sentinel intersection, before any network
  // fetch, so they never reach the initial document.
  prefetched?: CardRecord[];
  // Heading level for appended card titles. Must match whatever the SSR'd
  // StickerGrid above used, or the outline gains a level halfway down the
  // page. See `StickerCardProps.titleAs`.
  cardTitleAs?: "h2" | "h3";
}

export function LoadMoreClient({
  initialPage,
  initialHasNext,
  fetcher,
  withInGridAds = false,
  initialRecordCount = 0,
  prefetched,
  cardTitleAs,
}: LoadMoreClientProps) {
  const [items, setItems] = useState<CardRecord[]>([]);
  const [page, setPage] = useState(initialPage);
  const [hasNext, setHasNext] = useState(initialHasNext);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // useTransition's `pending` flips on the NEXT tick, so observer callbacks
  // can fire twice before React schedules the transition. Track in a ref
  // we update synchronously to dedupe.
  const inFlight = useRef(false);
  // Records the server pre-attached but didn't SSR. Drained on the first
  // intersection before any network fetch.
  const [pendingPrefetch, setPendingPrefetch] = useState<CardRecord[]>(() => prefetched ?? []);
  const hasPrefetchOrNext = hasNext || pendingPrefetch.length > 0;

  function loadMore() {
    if (inFlight.current) return;
    if (pendingPrefetch.length > 0) {
      setItems((prev) => [...prev, ...pendingPrefetch]);
      setPendingPrefetch([]);
      return;
    }
    if (!hasNext) return;
    inFlight.current = true;
    setError(null);
    startTransition(async () => {
      try {
        const next = page + 1;
        const result = await fetcher(next);
        setItems((prev) => [...prev, ...(result.records ?? [])]);
        setHasNext(result.hasNext);
        setPage(next);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load more");
      } finally {
        inFlight.current = false;
      }
    });
  }

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasPrefetchOrNext) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            loadMore();
            break;
          }
        }
      },
      // Start fetching ~600px before the sentinel reaches the viewport so the
      // user rarely sees the spinner — the next page is usually ready.
      { rootMargin: "600px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // Re-arm when state the observer closure depends on changes — including
    // pendingPrefetch length so the first intersection drains it, then a
    // subsequent intersection proceeds to the network fetch with a fresh
    // closure that sees the cleared prefetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNext, page, pendingPrefetch.length]);

  // Walk appended records once, interleaving ads at the same cadence as
  // StickerGrid. The starting count is what page 1 already rendered, so the
  // cap (IN_GRID_MAX_PER_PAGE inside the helper) is honoured across pages.
  const renderedItems: React.ReactNode[] = [];
  if (items.length > 0) {
    const initialAdsCount = withInGridAds ? inGridAdsForRecordCount(initialRecordCount) : 0;
    let adsInserted = initialAdsCount;
    items.forEach((record, idx) => {
      const absoluteIdx = initialRecordCount + idx;
      renderedItems.push(
        <StickerCard
          key={`${record.post_Id}-${idx}`}
          record={record}
          index={initialPage * 20 + idx}
          titleAs={cardTitleAs}
        />,
      );
      if (withInGridAds && shouldInsertInGridAdAfter(absoluteIdx, adsInserted)) {
        renderedItems.push(inGridAdNode(`ad-load-${absoluteIdx}`));
        adsInserted += 1;
      }
    });
  }

  return (
    <>
      {items.length > 0 && <div className="mt-6 flex flex-col gap-6">{renderedItems}</div>}
      {hasPrefetchOrNext ? (
        <>
          <div ref={sentinelRef} aria-hidden className="h-1 w-full" />
          <div className="mt-6 flex flex-col items-center gap-2" role="status" aria-live="polite">
            {pending ? (
              <span className="text-muted-foreground inline-flex items-center gap-2 text-[13px]">
                <span
                  aria-hidden
                  className="border-border border-t-brand-500 inline-block h-4 w-4 animate-spin rounded-full border-2"
                />
                Loading more sticker packs…
              </span>
            ) : null}
            {error ? (
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm text-red-600">{error}</p>
                <button
                  type="button"
                  onClick={loadMore}
                  className={cn(
                    "rounded-pill bg-brand-500 px-5 py-1.5 text-[13px] font-medium text-white transition",
                    "hover:bg-brand-600 active:bg-brand-700",
                  )}
                >
                  Try again
                </button>
              </div>
            ) : null}
          </div>
        </>
      ) : items.length > 0 ? (
        <p className="text-muted-foreground mt-8 text-center text-[13px]">
          You&rsquo;ve reached the end.
        </p>
      ) : null}
    </>
  );
}
